import React, { useMemo, useState } from "react";
import {
  Drawer,
  Card,
  CardContent,
  Badge,
  Divider,
  Typography,
  IconButton,
  Grid,
  Chip,
  CircularProgress,
  Button,
  Box,
  Dialog,
  DialogContent,
  MenuItem,
  DialogContentText,
  List,
  Avatar,
  Stack,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useStyles } from "../../theme/appStyles";
import dayjs, { Dayjs } from "dayjs";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import { useNotifier } from "../../Core/Notifier";
import useIsMobile from "../../Core/hook/useIsMobile";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  IToDo,
  ICustomer,
  ICustomerNote,
  IUserAdmin,
  IPolicy,
  IProductPopUP,
  IUserInfo,
  IMeeting,
  ITeam,
  ITasks,
} from "../../Api/Interface/api.interface";
import {
  useGetAllBirthdayDhashboardQuery,
  useGetDhashboardNoteQuery,
  useGetUserInfoOnyQuery,
  useGetAllPolicyFilterQuery,
  useGetAllPolicyGroupByQuery,
  useGetProductQuery,
  useGetCustomerQuery,
  useGetUserQuery,
  useGetMeetingQuery,
  useGetTeamQuery,
  useGetConversationsDhashNotificationQuery,
} from "../../Api/attoDeskApi";
import { Link } from "react-router-dom";
import GroupIcon from "@mui/icons-material/Group";
import { useNavigate } from "react-router-dom";
import { formatNumber } from "../formatNumber/formatNumber";
import { toProperCase } from "../Validation/toProperCase";

interface IProfileProps {
  notification: HTMLElement | null;
  onClose: () => void;
  open: boolean;
  notificationData: IToDo[];
}

const formatTime = (time: string | undefined) => {
  if (!time) return "";
  const [hour, minute] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hour, minute);
  return new Intl.DateTimeFormat("en-CA", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  }).format(date);
};

const headerHeight = 75;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  minHeight: headerHeight,
  padding: theme.spacing(0, 1),
  backgroundColor: theme.palette.topbarButton.drawerHeader,
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
  flexShrink: 0,
}));

const DrawerContent = styled("div")(({ theme }) => ({
  overflowY: "auto",
  flexGrow: 1,
  "&::-webkit-scrollbar": {
    width: "0px",
    color: "#fcf2eb",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#fcf2eb",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#fcf2eb",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
}));

const Profile: React.FC<IProfileProps> = ({
  notification,
  onClose,
  open,
  notificationData,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobileRes = useIsMobile();
  const { showErrorMessage, showMessage } = useNotifier();
  const navigate = useNavigate();
  const [paymentMonthYear, setPaymentMonthYear] = useState<Dayjs>(dayjs());

  const { data: userIdData } = useGetUserQuery();

  const {
    data: noificationData,
    isLoading: notificatinIsLoading,
    isError: notificationIsError,
  } = useGetConversationsDhashNotificationQuery({
    searchText: "",
    staffId: "",
    id: "",
  });

  const userId = React.useMemo(() => {
    if (userIdData?.data) {
      const userInfo = userIdData.data as IUserInfo;
      return userInfo.staffId;
    }
    return "";
  }, [userIdData]);

  const {
    data: birthdayData,
    isLoading: birthdayIsLoading,
    isError: birthdayIsError,
  } = useGetAllBirthdayDhashboardQuery({
    searchText: "",
    policyCustomerId: "",
    id: "",
  });

  const {
    data: noteData,
    isLoading: noteIsLoading,
    isError: noteIsError,
  } = useGetDhashboardNoteQuery({
    id: "",
  });

  const { data: userData, isLoading: userIsLoading } = useGetUserInfoOnyQuery({
    searchText: "",
  });

  const { data: allClientData, isLoading: allClientIsLoading } =
    useGetAllPolicyGroupByQuery({
      searchText: "",
      id: "",
      policyCustomerId: "",
    });

  const { data: allClientCovrageData, isLoading: allClientcoverageIsLoading } =
    useGetAllPolicyFilterQuery({
      searchText: "",
      policyCustomerId: "",
      createdDateStart: null,
      createdDateEnd: null,
      createdDate: null,
      policyCategoryId: "",
      policyInsuranceCompanyId: "",
      policyProductId: "",
      contengentPolicyOwnerId: "",
      jointPolicyOwnerId: "",
      policyIssueDate: null,
      policyRenewalDate: null,
      policyPaidUpDate: null,
      policyExpiryDate: null,
      policyCoversionDate: null,
      policyIsLifeTimeExpiryDate: null,
      policyPayor: "",
    });

  const {
    data: productData,
    isLoading: productDataIsLoading,
    isError: productDataError,
  } = useGetProductQuery({
    searchText: "",
    id: "",
    productBrandId: null,
    productCategoryId: null,
    productTagIds: null,
    productViewOnline: null,
    createdDateStart: null,
    createdDateEnd: null,
    createdDate: null,
  });

  const { data: clientData, isLoading: cilentIsLoading } = useGetCustomerQuery({
    searchText: "",
    id: "",
  });

  const { data, isLoading, isError, refetch } = useGetMeetingQuery({
    searchText: "",
    staffId: userId || "",
    clientId: "",
    id: "",
    meeting_Date: paymentMonthYear.format("YYYY-MM-DD"),
  });

  const { data: teamData, isLoading: teamsLoading } = useGetTeamQuery({
    searchText: "",
    id: "",
  });

  const userList = useMemo(() => {
    return userData?.data as IUserAdmin[];
  }, [userData?.data]);

  const userMap = useMemo(() => {
    const map = new Map();
    userList?.forEach((user) => {
      map.set(user.id, `${user.user_role_name}`);
    });
    return map;
  }, [userList]);

  const todaysBirthdaysList = useMemo(() => {
    return birthdayData?.todaysBirthdays as ICustomer[];
  }, [birthdayData?.todaysBirthdays]);

  const noteList = useMemo(() => {
    return noteData?.data as ICustomerNote[];
  }, [noteData?.data]);

  const allCliendUpList = useMemo(() => {
    return (allClientData?.data ?? []) as IPolicy[];
  }, [allClientData?.data]);

  const allCliendUpCoverageList = useMemo(() => {
    return (allClientCovrageData?.data ?? []) as IPolicy[];
  }, [allClientCovrageData?.data]);

  const productList = useMemo(() => {
    return productData?.data as IProductPopUP[];
  }, [productData?.data]);

  const cliendList = useMemo(() => {
    return (clientData?.data ?? []) as ICustomer[];
  }, [clientData?.data]);

  const teamList = useMemo(() => {
    return (teamData?.data ?? []) as ITeam[];
  }, [teamData?.data]);

  const allClientMap = useMemo(() => {
    const map = new Map();
    allCliendUpList?.forEach((category) => {
      map.set(category.id, category.policyNumber);
    });
    return map;
  }, [allCliendUpList]);

  const productMap = useMemo(() => {
    const map = new Map();
    productList?.forEach((brand) => {
      map.set(brand.id, brand.productName);
    });
    return map;
  }, [productList]);

  const staffMap = useMemo(() => {
    const map = new Map();
    teamList?.forEach((category) => {
      map.set(
        category.id,
        toProperCase(category.teamFirstName || "") +
          " " +
          toProperCase(category.teamLastName || "")
      );
    });
    return map;
  }, [teamList]);

  // const meetingList: IMeeting[] = Array.isArray(data?.data)
  //   ? (data?.data as IMeeting[])
  //   : [];

  const meetingList = useMemo(() => {
    return data?.data as IMeeting[];
  }, [data?.data]);

  const allClientCoveargeMap = useMemo(() => {
    const map = new Map();

    allCliendUpCoverageList?.forEach((item) => {
      const productName = productMap.get(item.policyProductId) || "";

      map.set(
        item.id,
        `${item.policyNumber}-${productName} - ${formatNumber(
          item.policyCoverageAmount ?? 0
        )}`
      );
    });

    return map;
  }, [allCliendUpCoverageList, productMap]);

  const clientMap = useMemo(() => {
    const map = new Map();
    cliendList?.forEach((category) => {
      map.set(
        category.id,
        toProperCase(category.customerFirstName || "") +
          " " +
          toProperCase(category.customerLastName || "")
      );
    });
    return map;
  }, [cliendList]);

  const taskList: ITasks[] = Array.isArray(noificationData?.tasksData)
    ? (noificationData?.tasksData as ITasks[])
    : [];

  const renualPolicyDataList: IPolicy[] = Array.isArray(
    noificationData?.todayrenualPolicyData
  )
    ? (noificationData?.todayrenualPolicyData as IPolicy[])
    : [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "80%", sm: 450 },
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <DrawerHeader>
        <Grid container spacing={2}>
          <Grid
            item
            lg={12}
            md={12}
            sm={12}
            xs={12}
            className={classes.drawerNotification}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "16px",
                flexShrink: 0,
              }}
            >
              <Typography variant="h5" className={classes.drawerHeaderText}>
                Notifications
              </Typography>
              <IconButton
                onClick={onClose}
                className={classes.drawerHeaderText}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </Grid>
        </Grid>
      </DrawerHeader>
      <DrawerContent>
        {notificationData && notificationData.length > 0 && (
          <Box p={1}>
            <Card variant="outlined" className={classes.card} sx={{ p: 2 }}>
              <Typography className={classes.dashboardfristCardHeading} mb={2}>
                Conversations
              </Typography>

              {notificationData.length === 0 ? (
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    py={2}
                  >
                    No unread conversations.
                  </Typography>
                </Grid>
              ) : (
                notificationData.map((group: IToDo) => (
                  <Grid item xs={12} key={group.id}>
                    <Card
                      onClick={() => {
                        navigate(`/conversations/messages/${group.id}`);
                        onClose();
                      }}
                      elevation={0}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 2,
                        borderRadius: 2,
                        cursor: "pointer",
                        transition: "background 0.2s",
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Badge
                        badgeContent={
                          group.unread_count > 0 ? group.unread_count : null
                        }
                        color="primary"
                        sx={{
                          mr: 2,
                          "& .MuiBadge-badge": {
                            fontSize: "0.75rem",
                            minWidth: 20,
                            height: 20,
                            borderRadius: "50%",
                            backgroundColor: "green",
                            color: "#fff",
                          },
                        }}
                      >
                        <Avatar sx={{ bgcolor: "primary.main" }}>
                          {group.subject ? (
                            group.subject.charAt(0).toUpperCase()
                          ) : (
                            <GroupIcon />
                          )}
                        </Avatar>
                      </Badge>

                      <Grid container direction="column">
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {group.subject || "Unnamed Group"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={
                            group.unread_count > 0 ? "green" : "text.secondary"
                          }
                          fontWeight={group.unread_count > 0 ? 600 : 400}
                        >
                          {group.unread_count > 0
                            ? `You have ${group.unread_count} unread ${
                                group.unread_count === 1
                                  ? "message"
                                  : "messages"
                              }`
                            : "No new messages"}
                        </Typography>
                      </Grid>
                    </Card>
                  </Grid>
                ))
              )}
            </Card>
          </Box>
        )}

        {meetingList && meetingList.length > 0 && (
          <Box p={1}>
            <Card variant="outlined" className={classes.card} sx={{ p: 2 }}>
              <Typography className={classes.dashboardfristCardHeading} mb={2}>
                Today’s Meetings
              </Typography>

              {isError ? (
                <Box textAlign="center" py={4}>
                  <Typography color="error">Error fetching data</Typography>
                </Box>
              ) : isLoading || teamsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {meetingList.length === 0 ? (
                    <Box textAlign="center" py={4}>
                      <Typography>No meetings scheduled today</Typography>
                    </Box>
                  ) : (
                    <List sx={{ width: "100%" }}>
                      {meetingList.map((row, index) => (
                        <Grid item xs={12} key={row.id}>
                          <Link
                            to={`/meeting/profile/${row.id}`}
                            className={classes.iconLinkPropNormal}
                          >
                            <Card
                              onClick={() => {
                                navigate(`/meeting/profile/${row.id}`);
                              }}
                              elevation={0}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                p: 2,
                                borderRadius: 2,
                                cursor: "pointer",
                                backgroundColor: theme.palette.cardAdd.bgColor,
                                transition: "background 0.2s",
                                "&:hover": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor:
                                    row?.meeting_category_color ||
                                    "primary.main",
                                }}
                              >
                                {row?.subject?.charAt(0).toUpperCase() || (
                                  <GroupIcon />
                                )}
                              </Avatar>
                              <Grid container direction="column" pl={1}>
                                <Typography
                                  // variant="subtitle1"
                                  fontWeight={600}
                                  noWrap
                                >
                                  {row.subject || ""}
                                </Typography>
                                {(row?.clientId ||
                                  row?.clientId ||
                                  row?.meeting_Date ||
                                  row?.meeting_time) && (
                                  <Typography
                                    color="text.secondary"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {[
                                      row?.clientId
                                        ? clientMap.get(Number(row.clientId))
                                        : null,
                                      row?.staffId
                                        ? staffMap.get(Number(row.staffId))
                                        : null,
                                      row?.meeting_Date
                                        ? ` ${dayjs(row.meeting_Date).format(
                                            "MMM D, YYYY"
                                          )}`
                                        : null,
                                      row?.meeting_time
                                        ? ` ${formatTime(row.meeting_time)}`
                                        : null,
                                    ]
                                      .filter(Boolean)
                                      .map((text, idx, arr) => (
                                        <span key={idx}>
                                          {text}
                                          {idx < arr.length - 1 && " | "}
                                        </span>
                                      ))}
                                  </Typography>
                                )}
                              </Grid>
                            </Card>
                          </Link>
                        </Grid>
                      ))}
                    </List>
                  )}
                </>
              )}
            </Card>
          </Box>
        )}

        {taskList && taskList.length > 0 && (
          <Box p={1}>
            <Card variant="outlined" className={classes.card} sx={{ p: 2 }}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography className={classes.dashboardfristCardHeading}>
                  Tasks Due Today
                </Typography>
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                {notificationIsError ? (
                  <Box textAlign="center" py={4}>
                    <Typography color="error">Error fetching data</Typography>
                  </Box>
                ) : notificatinIsLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {taskList.length === 0 ? (
                      <Box textAlign="center" py={4}>
                        <Typography>No task due today</Typography>
                      </Box>
                    ) : (
                      <List sx={{ width: "100%" }}>
                        {taskList.map((row, index) => (
                          <Grid item xs={12} key={row.id}>
                            <Link
                              to={`/task/profile/${row.id}`}
                              className={classes.iconLinkPropNormal}
                            >
                              <Card
                                onClick={() => {
                                  navigate(`/task/profile/${row.id}`);
                                }}
                                elevation={0}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  p: 2,
                                  borderRadius: 2,
                                  cursor: "pointer",
                                  backgroundColor:
                                    theme.palette.cardAdd.bgColor,
                                  transition: "background 0.2s",
                                  "&:hover": {
                                    backgroundColor: theme.palette.action.hover,
                                  },
                                }}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor:
                                      row?.meeting_category_color ||
                                      "primary.main",
                                  }}
                                >
                                  {row?.subject?.charAt(0).toUpperCase() || (
                                    <GroupIcon />
                                  )}
                                </Avatar>
                                <Grid container direction="column" pl={1}>
                                  <Typography
                                    // variant="subtitle1"
                                    fontWeight={600}
                                    noWrap
                                  >
                                    {row.subject || ""}
                                  </Typography>
                                  {(row?.clientId ||
                                    row?.clientId ||
                                    row?.end_Date ||
                                    row?.meeting_time) && (
                                    <Typography
                                      color="text.secondary"
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {[
                                        row?.clientId
                                          ? clientMap.get(Number(row.clientId))
                                          : null,
                                        row?.staffId
                                          ? staffMap.get(Number(row.staffId))
                                          : null,
                                        row?.end_Date
                                          ? ` ${dayjs(row.end_Date).format(
                                              "MMM D, YYYY"
                                            )}`
                                          : null,
                                        row?.meeting_time
                                          ? ` ${formatTime(row.meeting_time)}`
                                          : null,
                                      ]
                                        .filter(Boolean)
                                        .map((text, idx, arr) => (
                                          <span key={idx}>
                                            {text}
                                            {idx < arr.length - 1 && " | "}
                                          </span>
                                        ))}
                                    </Typography>
                                  )}
                                </Grid>
                              </Card>
                            </Link>
                          </Grid>
                        ))}
                      </List>
                    )}
                  </>
                )}
              </Grid>
            </Card>
          </Box>
        )}

        {todaysBirthdaysList && todaysBirthdaysList.length > 0 && (
          <Box p={1}>
            <Card variant="outlined" className={classes.card} sx={{ p: 2 }}>
              <Typography className={classes.dashboardfristCardHeading} mb={2}>
                Today's Birthday
              </Typography>

              {birthdayIsLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="10vh"
                >
                  <CircularProgress />
                </Box>
              ) : birthdayIsError || todaysBirthdaysList.length === 0 ? (
                <Typography sx={{ textAlign: "center", mt: 2 }}>
                  {birthdayIsError
                    ? "Error fetching data"
                    : "No new notifications"}
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {todaysBirthdaysList.map((customer: ICustomer, index) => (
                    <Grid item xs={12} key={index}>
                      <Link
                        to={`/profile/customer/${customer.id}`}
                        className={classes.iconLinkPropNormal}
                        style={{ textDecoration: "none" }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          p={1}
                          bgcolor={theme.palette.cardAdd.bgColor}
                          borderRadius={1}
                          sx={{
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <Badge
                            color="primary"
                            sx={{
                              mr: 2,
                              "& .MuiBadge-badge": {
                                fontSize: "12px",
                                minWidth: 15,
                                height: 15,
                                borderRadius: "50%",
                                backgroundColor: "green",
                                color: "#fff",
                              },
                            }}
                          >
                            <Avatar sx={{ bgcolor: "primary.main" }}>
                              {customer.customerFirstName ||
                              customer.customerLastName ? (
                                `${customer.customerFirstName ?? ""} ${
                                  customer.customerLastName ?? ""
                                }`
                                  .trim()
                                  .charAt(0)
                                  .toUpperCase()
                              ) : (
                                <GroupIcon />
                              )}
                            </Avatar>
                          </Badge>

                          <Box>
                            <Typography>
                              {customer.customerFirstName}{" "}
                              {customer.customerLastName}
                            </Typography>
                          </Box>
                        </Box>
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Card>
          </Box>
        )}

        {renualPolicyDataList && renualPolicyDataList.length > 0 && (
          <Box p={1}>
            <Card variant="outlined" className={classes.card} sx={{ p: 2 }}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography className={classes.dashboardfristCardHeading}>
                  Today’s Policy Renewals
                </Typography>
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                {notificationIsError ? (
                  <Box textAlign="center" py={4}>
                    <Typography color="error">Error fetching data</Typography>
                  </Box>
                ) : notificatinIsLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {renualPolicyDataList.length === 0 ? (
                      <Box textAlign="center" py={4}>
                        <Typography>No task due today</Typography>
                      </Box>
                    ) : (
                      <List sx={{ width: "100%" }}>
                        {renualPolicyDataList.map((row, index) => (
                          <Grid item xs={12} key={row.id}>
                            <Link
                              to={`/customer/${row.policyCustomerId}/policy/${row.policyNumber}`}
                              className={classes.iconLinkPropNormal}
                            >
                              <Card
                                onClick={() => {
                                  navigate(
                                    `/customer/${row.policyCustomerId}/policy/${row.policyNumber}`
                                  );
                                }}
                                elevation={0}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  p: 2,
                                  borderRadius: 2,
                                  cursor: "pointer",
                                  backgroundColor:
                                    theme.palette.cardAdd.bgColor,
                                  transition: "background 0.2s",
                                  "&:hover": {
                                    backgroundColor: theme.palette.action.hover,
                                  },
                                }}
                              >
                                <Grid container direction="column" pl={1}>
                                  <Typography
                                    // variant="subtitle1"
                                    fontWeight={600}
                                    noWrap
                                  >
                                    {row.policyNumber || ""}
                                  </Typography>
                                  {(row?.policyCustomerId ||
                                    row?.policyRenewalDate) && (
                                    <Typography
                                      color="text.secondary"
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {[
                                        row?.policyCustomerId
                                          ? clientMap.get(
                                              Number(row.policyCustomerId)
                                            )
                                          : null,
                                        row?.policyRenewalDate
                                          ? ` ${dayjs(
                                              row.policyRenewalDate
                                            ).format("MMM D, YYYY")}`
                                          : null,
                                      ]
                                        .filter(Boolean)
                                        .map((text, idx, arr) => (
                                          <span key={idx}>
                                            {text}
                                            {idx < arr.length - 1 && " | "}
                                          </span>
                                        ))}
                                    </Typography>
                                  )}
                                </Grid>
                              </Card>
                            </Link>
                          </Grid>
                        ))}
                      </List>
                    )}
                  </>
                )}
              </Grid>
            </Card>
          </Box>
        )}

        {noteList && noteList.length > 0 && (
          <Box p={1}>
            <Card variant="outlined" className={classes.card} sx={{ p: 2 }}>
              <Typography className={classes.dashboardfristCardHeading} mb={2}>
                Today's Note
              </Typography>

              {userIsLoading ||
              noteIsLoading ||
              productDataIsLoading ||
              allClientcoverageIsLoading ||
              allClientIsLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="10vh"
                >
                  <CircularProgress />
                </Box>
              ) : noteIsError || noteList.length === 0 ? (
                <Typography sx={{ textAlign: "center", mt: 2 }}>
                  {noteIsError ? "Error fetching data" : "No new notifications"}
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {noteList.map((msg: ICustomerNote, index) => {
                    const clientName = clientMap.get(msg.clientId);

                    return (
                      <Grid
                        item
                        xs={12}
                        key={msg.id}
                        container
                        justifyContent="flex-start"
                        onClick={() => {
                          navigate(`/profile/customer/${msg.clientId}`);
                          onClose();
                        }}
                        sx={{ cursor: "pointer" }}
                      >
                        <Grid
                          item
                          xs={12}
                          sx={{
                            maxWidth: "100%",
                            padding: 2,
                            backgroundColor: "#fff",
                            borderRadius: 3,
                            boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                            position: "relative",
                            alignSelf: "flex-start",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: "primary.main",
                              marginBottom: 1,
                            }}
                          >
                            {`${clientName}`}
                          </Typography>

                          {/* Note */}
                          {msg.note && (
                            <Typography
                              component="div"
                              dangerouslySetInnerHTML={{ __html: msg.note }}
                              sx={{
                                color: "#333",
                                fontSize: "14px",
                                lineHeight: "1.6",
                                marginTop: 0,
                              }}
                            />
                          )}

                          {/* Policies */}
                          {msg.policyIds.length > 0 && (
                            <Stack
                              direction="row"
                              flexWrap="wrap"
                              sx={{
                                maxHeight: 200,
                                overflowY: "auto",
                                mt: 1,
                              }}
                            >
                              {msg.policyIds.map((id) => {
                                const name =
                                  allClientMap.get(id) ?? `Policy #${id}`;
                                return (
                                  <Chip
                                    key={id}
                                    label={`@ ${name}`}
                                    sx={{
                                      margin: 0.5,
                                      color: "#1565c0",
                                      cursor: "pointer",
                                      backgroundColor: "#e3f2fd",
                                      fontWeight: 600,
                                      fontSize: "13px",
                                      "&:hover": {
                                        backgroundColor: "#bbdefb",
                                      },
                                    }}
                                  />
                                );
                              })}
                            </Stack>
                          )}

                          {/* Coverages */}
                          {msg.coverageIds.length > 0 && (
                            <>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, marginBottom: 1 }}
                              >
                                Coverages
                              </Typography>

                              <Stack
                                direction="row"
                                flexWrap="wrap"
                                sx={{
                                  maxHeight: 200,
                                  overflowY: "auto",
                                }}
                              >
                                {msg.coverageIds.map((id) => {
                                  const name =
                                    allClientCoveargeMap.get(id) ??
                                    `Coverage #${id}`;
                                  return (
                                    <Chip
                                      key={id}
                                      label={`@ ${name}`}
                                      sx={{
                                        margin: 0.5,
                                        color: "#1565c0",
                                        cursor: "pointer",
                                        backgroundColor: "#e3f2fd",
                                        fontWeight: 600,
                                        fontSize: "13px",
                                        "&:hover": {
                                          backgroundColor: "#bbdefb",
                                        },
                                      }}
                                    />
                                  );
                                })}
                              </Stack>
                            </>
                          )}

                          {/* Date */}
                          <Grid
                            item
                            sx={{
                              textAlign: "right",
                              marginTop: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ color: "primary.main" }}
                            >
                              {`${userMap.get(msg.updated_by) || ""} | ${dayjs(
                                msg.updated_at
                              ).format("h:mm A")}`}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Card>
          </Box>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default Profile;
